import { useSettings } from "@/data/Settings";
import { LatLng, StoreSearchAutoCompleteOption } from "@/data/types/Store";
import useSWR from "swr";

const DATA_KEY = "LOCATION_IQ_AUTOCOMPLETE";

const fetcher =
	async (
		serviceUrl: string,
		params: Record<string, any>
	) => {
		var url = new URL(serviceUrl)
		url.search = new URLSearchParams(params).toString();
		return await fetch(url.toString())
			.then(response => {
				if(!response.ok && response.status!==404){
					console.log("API request failed in LocationIQ fetcher, response: ",response);
					throw new Error("LocationIQ API response is !ok");
				}
				return response.json()
			})
			.catch((error) => console.log("API request failed in LocationIQ fetcher, error: ",error));
	};

const dataMap = (data: any): { lqAutoCompleteOtions: StoreSearchAutoCompleteOption[] } => {
	return {
		lqAutoCompleteOtions: data && data.length > 0 ?
			data.map((elem: any) => {
				return {
					address: elem.address,
					lat: elem.lat,
					lon: elem.lon,
					display_name: elem.display_name,
					place_id: elem.place_id
				}
			}) : []
	}
};


const zipCodeDataMap = (data: any) => {	
	let tempLatLng:LatLng = {
		lat: data?.at(0)?.lat,
		lng: data?.at(0)?.lon,
	}
	return tempLatLng;
};

export const useLocationIQAutoComplete = (searchKey: string) => {
	const { settings } = useSettings();
	const constants = settings.userData;
	let resultLimit: number = constants.Locationiq_results_limit ? parseInt(constants.Locationiq_results_limit) : 25;
	const locationIQTags = constants.Locationiq_tags_list;
	let params = {
		key: constants.LocationIQKey, 
		format: "json",
		limit: resultLimit,
		countrycodes: 'nl',
		'accept-language': 'nl',
		tag: locationIQTags,
		viewbox: constants.Locationiq_viewbox_coordinates,
		bounded: 1,
		dedupe: 1,
		q: searchKey
	}
	const { data } = useSWR(
		searchKey && searchKey.length > 2 
			? [
				{
					url: constants.Locationiq_Service_URL, 
					params 
				}, DATA_KEY
			] : null,
		async ([props]) => fetcher(props.url, props.params));
	return dataMap(data);
};

export const useLocationIQByZipCode = (zipCode: string) => {
	const { settings } = useSettings();
	const constants = settings.userData;
	let resultLimit: number = constants.Locationiq_results_limit ? parseInt(constants.Locationiq_results_limit) : 15;
	let params = {
		key: constants.LocationIQKey, 
		format: "json",
		limit: resultLimit,
		countrycodes: 'nl',
		'accept-language': 'nl',
		tag: 'place:city,place:village,place:town,place:island,place:suburb,place:state,boundary:administrative,place:postcode,highway:*,cycleway:*',
		viewbox: constants.Locationiq_viewbox_coordinates,
		bounded: 1,
		dedupe: 1,
		postalcode: zipCode
	}
	const { data } = useSWR(
		zipCode && zipCode.length > 2 
			? [
				{
					url: constants.Locationiq_Search_URL, 
					params 
				}, DATA_KEY
			] : null,
		async ([props]) => fetcher(props.url, props.params));
	return zipCodeDataMap(data);
};

export const useLocationReverseGeocode = (latitude: string, longitude: string) => {
	const { settings } = useSettings();
	const constants = settings.userData;
	let params = {
		key: constants.LocationIQKey, 
		format: "json",
		lat: latitude,
		lon: longitude
	}
	const { data } = useSWR(
		latitude && longitude
			? [
				{
					url: constants.LocationiqReverseGeocodingURL,
					params
				}, DATA_KEY
			] : null,
		async ([props]) => fetcher(props.url, props.params));
	return (
		data?.address?.suburb ?? 
		data?.address?.city ??
		data?.address?.state ??
		""
	);
};

